/**
 * Client Portal - Invoice Detail
 *
 * Shows invoice details with line items.
 * Read-only view - no payment functionality.
 *
 * @see Story 4.4: Invoice Viewing in Client Portal
 */

import { Button } from "@designs-by-maryam/ui/components/button";
import { Separator } from "@designs-by-maryam/ui/components/separator";
import { Skeleton } from "@designs-by-maryam/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, FileText, Printer } from "lucide-react";
import { requireClient } from "@/functions/require-role";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/portal/invoices/$id")({
	component: InvoiceDetail,
	beforeLoad: async () => {
		await requireClient();
	},
});

type InvoiceStatus = "draft" | "pending" | "paid" | "overdue" | "cancelled";

const STATUS_CONFIG: Record<
	InvoiceStatus,
	{ label: string; color: string; bgColor: string }
> = {
	draft: {
		label: "Draft",
		color: "text-gray-700 dark:text-gray-300",
		bgColor: "bg-gray-100 dark:bg-gray-800",
	},
	pending: {
		label: "Pending Payment",
		color: "text-yellow-700 dark:text-yellow-300",
		bgColor: "bg-yellow-100 dark:bg-yellow-900/50",
	},
	paid: {
		label: "Paid",
		color: "text-green-700 dark:text-green-300",
		bgColor: "bg-green-100 dark:bg-green-900/50",
	},
	overdue: {
		label: "Overdue",
		color: "text-red-700 dark:text-red-300",
		bgColor: "bg-red-100 dark:bg-red-900/50",
	},
	cancelled: {
		label: "Cancelled",
		color: "text-gray-500 dark:text-gray-400",
		bgColor: "bg-gray-100 dark:bg-gray-800",
	},
};

function InvoiceDetail() {
	const { id } = Route.useParams();

	const { data: invoice, isLoading } = useQuery(
		orpc.portalGetInvoiceDetails.queryOptions({ input: { invoiceId: id } }),
	);

	const { data: profile } = useQuery(orpc.portalGetMyProfile.queryOptions());

	if (isLoading) {
		return <InvoiceDetailSkeleton />;
	}

	if (!invoice) {
		return (
			<div className="container mx-auto px-4 py-8">
				<div className="rounded-lg border bg-card p-12 text-center">
					<FileText className="mx-auto h-12 w-12 text-muted-foreground" />
					<h3 className="mt-4 font-semibold text-lg">Invoice not found</h3>
					<Link to="/portal/invoices">
						<Button className="mt-4" variant="outline">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Back to Invoices
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	const status =
		STATUS_CONFIG[invoice.status as InvoiceStatus] || STATUS_CONFIG.pending;

	return (
		<div className="container mx-auto px-4 py-8 md:px-6">
			{/* Back Button */}
			<Link
				className="mb-6 inline-flex items-center text-muted-foreground hover:text-foreground"
				to="/portal/invoices"
			>
				<ArrowLeft className="mr-2 h-4 w-4" />
				Back to Invoices
			</Link>

			{/* Invoice Card */}
			<div className="mx-auto max-w-3xl rounded-xl border bg-card shadow-sm">
				{/* Header */}
				<div className="border-b p-6 md:p-8">
					<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
						<div>
							<h1 className="font-bold text-2xl">
								Invoice {invoice.invoiceNumber}
							</h1>
							<p className="mt-1 text-muted-foreground">
								Issued on {new Date(invoice.createdAt).toLocaleDateString()}
							</p>
						</div>
						<span
							className={`inline-flex rounded-full px-3 py-1 font-medium text-sm ${status.bgColor} ${status.color}`}
						>
							{status.label}
						</span>
					</div>

					{/* Actions */}
					<div className="mt-4 flex gap-2">
						<Button onClick={() => window.print()} size="sm" variant="outline">
							<Printer className="mr-2 h-4 w-4" />
							Print
						</Button>
					</div>
				</div>

				{/* Bill To */}
				<div className="border-b p-6 md:p-8">
					<div className="grid gap-6 md:grid-cols-2">
						<div>
							<h3 className="mb-2 font-medium text-muted-foreground text-sm uppercase">
								From
							</h3>
							<p className="font-medium">Designs by Maryam</p>
							<p className="text-muted-foreground text-sm">
								Manama, Kingdom of Bahrain
							</p>
							<p className="text-muted-foreground text-sm">
								hello@designsbymaryam.com
							</p>
						</div>
						<div>
							<h3 className="mb-2 font-medium text-muted-foreground text-sm uppercase">
								Bill To
							</h3>
							<p className="font-medium">
								{profile?.companyName || profile?.contactName}
							</p>
							<p className="text-muted-foreground text-sm">{profile?.email}</p>
							{profile?.address && (
								<p className="text-muted-foreground text-sm">
									{profile.address}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Invoice Details */}
				<div className="border-b p-6 md:p-8">
					<div className="grid gap-4 md:grid-cols-3">
						<div>
							<p className="font-medium text-muted-foreground text-sm">
								Invoice Number
							</p>
							<p className="font-medium">{invoice.invoiceNumber}</p>
						</div>
						<div>
							<p className="font-medium text-muted-foreground text-sm">
								Issue Date
							</p>
							<p className="font-medium">
								{new Date(invoice.createdAt).toLocaleDateString()}
							</p>
						</div>
						<div>
							<p className="font-medium text-muted-foreground text-sm">
								Due Date
							</p>
							<p className="font-medium">
								{invoice.dueDate
									? new Date(invoice.dueDate).toLocaleDateString()
									: "Upon Receipt"}
							</p>
						</div>
					</div>
				</div>

				{/* Line Items */}
				<div className="p-6 md:p-8">
					<table className="w-full">
						<thead>
							<tr className="border-b">
								<th className="pb-3 text-left font-medium text-muted-foreground text-sm">
									Description
								</th>
								<th className="pb-3 text-right font-medium text-muted-foreground text-sm">
									Qty
								</th>
								<th className="pb-3 text-right font-medium text-muted-foreground text-sm">
									Rate
								</th>
								<th className="pb-3 text-right font-medium text-muted-foreground text-sm">
									Amount
								</th>
							</tr>
						</thead>
						<tbody>
							{invoice.items.map((item) => (
								<tr className="border-b last:border-0" key={item.id}>
									<td className="py-3">{item.description}</td>
									<td className="py-3 text-right text-muted-foreground">
										{item.quantity}
									</td>
									<td className="py-3 text-right text-muted-foreground">
										{item.rateFormatted}
									</td>
									<td className="py-3 text-right font-medium">
										{item.amountFormatted}
									</td>
								</tr>
							))}
						</tbody>
					</table>

					{/* Totals */}
					<div className="mt-6 space-y-2 border-t pt-6">
						<div className="flex justify-between text-sm">
							<span className="text-muted-foreground">Subtotal</span>
							<span>{invoice.subtotalFormatted}</span>
						</div>
						{invoice.taxBhd > 0 && (
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Tax</span>
								<span>{invoice.taxFormatted}</span>
							</div>
						)}
						<Separator className="my-2" />
						<div className="flex justify-between font-bold text-lg">
							<span>Total</span>
							<span>{invoice.totalFormatted}</span>
						</div>
					</div>

					{/* Notes */}
					{invoice.notes && (
						<div className="mt-6 rounded-lg bg-muted/50 p-4">
							<p className="mb-1 font-medium text-sm">Notes</p>
							<p className="text-muted-foreground text-sm">{invoice.notes}</p>
						</div>
					)}

					{/* Payment Info */}
					{invoice.status === "paid" && invoice.paidDate && (
						<div className="mt-6 rounded-lg bg-green-50 p-4 dark:bg-green-950/30">
							<p className="font-medium text-green-700 text-sm dark:text-green-300">
								Payment received on{" "}
								{new Date(invoice.paidDate).toLocaleDateString()}
							</p>
						</div>
					)}

					{(invoice.status === "pending" || invoice.status === "overdue") && (
						<div className="mt-6 rounded-lg bg-yellow-50 p-4 dark:bg-yellow-950/30">
							<p className="font-medium text-sm text-yellow-700 dark:text-yellow-300">
								Payment pending. Please contact us for payment instructions.
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

function InvoiceDetailSkeleton() {
	return (
		<div className="container mx-auto px-4 py-8">
			<Skeleton className="mb-6 h-4 w-32" />
			<div className="mx-auto max-w-3xl rounded-xl border bg-card p-8">
				<Skeleton className="mb-2 h-8 w-48" />
				<Skeleton className="mb-8 h-4 w-32" />
				<div className="space-y-4">
					<Skeleton className="h-16 w-full" />
					<Skeleton className="h-16 w-full" />
					<Skeleton className="h-16 w-full" />
				</div>
			</div>
		</div>
	);
}
